import React from "react";
import { Card, Row, Col, Typography, Spin } from "antd";
import { useUserContextHook } from "../../hooks/useUserContextHook";
import moment from "moment";
import { GetTripPlanGroupedCountService } from "../../services/trip-plan-service";
import { useQuery } from "@tanstack/react-query";
import CountUp from "react-countup";

const { Text } = Typography;

export const useTripPlanGroupedCountQuery = ({
    cityIds,
    fromDate,
    toDate,
    enabled = true,
}: {
    cityIds: number[];
    fromDate: string;
    toDate: string;
    enabled?: boolean;
}) => {
    const onRequest = async () => {
        const res = await GetTripPlanGroupedCountService(cityIds, fromDate, toDate);
        if (res?.status === 200 && res?.data?.statusCode === 200) {
            const data = res.data?.data;

            const open = data.open ?? 0;
            const closed = data.closed ?? 0;
            const loadToDestination = data.loadToDestination ?? 0;
            const arrivedAtDestination = data.arrivedAtDestination ?? 0;

            data.totalTrips = open + closed + loadToDestination + arrivedAtDestination;
            return data;
        } else {
            return [];
        }
    };

    return useQuery({
        queryKey: ["trip-plan-dashboard-count", cityIds, fromDate, toDate],
        staleTime: 30000,
        queryFn: () => onRequest(),
        enabled: enabled && cityIds.length > 0 && !!fromDate && !!toDate,
    });
};

const metrics = [
    { key: "open", title: "Total Open Trips", value: 0 },
    { key: "closed", title: "Total Closed Trips", value: 0 },
    { key: "loadToDestination", title: "Total Load to Destination", value: 0 },
    { key: "arrivedAtDestination", title: "Total Arrived at Destination", value: 0 },
    { key: "totalTrips", title: "Total Trips", value: 0 },
    { key: "totalMasterPallets", title: "Total Master Pallets", value: 0 },
    { key: "totalWaybills", title: "Total Waybills", value: 0 },
    { key: "totalPieces", title: "Total Pieces", value: 0 },
    { key: "totalWeight", title: "Total Weight (kg)", value: "0" },
];

const labelColors = {
    "Total Open Trips": "#52c41a",
    "Total Closed Trips": "#f5222d",
    "Total Load to Destination": "#fa8c16",
    "Total Arrived at Destination": "#1890ff",
    "Total Trips": "#722ed1",
    "Total Master Pallets": "#13c2c2",
    "Total Waybills": "#eb2f96",
    "Total Pieces": "#2f54eb",
    "Total Weight": "#fa541c",
};

const DashboardRow = ({ filter, data }) => {
    const { masterDetail } = useUserContextHook();

    const filterInfo = React.useMemo(() => {
        const originNames = filter?.originList
            .map((id) => masterDetail?.city?.find((city) => city.cityId === id)?.city)
            .filter(Boolean)
            .join(", ");

        const [startDate, endDate] = filter?.startEndDate || [];
        const formattedStart = startDate ? moment(startDate).format("DD MMM YYYY") : "";
        const formattedEnd = endDate ? moment(endDate).format("DD MMM YYYY") : "";
        const dateRange = (formattedStart && formattedEnd) ? `${formattedStart} - ${formattedEnd}` : "Unknown";

        return {
            origin: originNames || "Unknown",
            dateRange,
        };
    }, [filter?.originList, filter?.startEndDate, masterDetail?.city]);

    const { data: groupedCountData, isLoading, isFetching } = useTripPlanGroupedCountQuery({
        cityIds: filter?.originList || [],
        fromDate: filter?.startEndDate?.[0]
            ? moment(filter.startEndDate[0]).utc().set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).format("YYYY-MM-DDTHH:mm:ss[Z]")
            : "",
        toDate: filter?.startEndDate?.[1]
            ? moment(filter.startEndDate[1]).utc().set({ hour: 23, minute: 59, second: 59, millisecond: 59 }).add(1, 'day').format("YYYY-MM-DDTHH:mm:ss[Z]")
            : "",
    });

    const DashboardData = React.useMemo(() => {
        if (!groupedCountData) return metrics.map((metric) => ({ ...metric, value: 0 }));

        return metrics.map((metric) => ({
            ...metric,
            value: groupedCountData[metric.key] || 0,
        }));
    }, [groupedCountData]);

    return (
        <Card
            bordered={false}
            style={{
                marginBottom: 16,
                borderRadius: 12,
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                background: "#fafafa",
            }}
            bodyStyle={{ padding: 12 }}
        >
            {/* Filter details inside body */}
            <div style={{ marginBottom: 12 }}>
                <Text type="secondary" style={{ fontSize: 12, marginRight: 16 }}>
                    Origin: <Text strong>{filterInfo.origin}</Text>
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    Date: <Text strong>{filterInfo.dateRange}</Text>
                </Text>
            </div>

            {/* Metric cards with animation and loading spinner */}
            <Spin spinning={isLoading || isFetching} tip="Loading..." size="small">
                <Row gutter={[8, 8]} wrap>
                    {DashboardData.map((item) => (
                        <Col key={item.title}>
                            <Card
                                bordered={false}
                                style={{
                                    width: 140,
                                    height: 52,
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    textAlign: "center",
                                    borderRadius: 10,
                                    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.08)",
                                    background: "#fff",
                                }}
                                bodyStyle={{
                                    padding: 4,
                                    lineHeight: 1.1,
                                }}
                            >
                                <div style={{ width: "100%" }}>
                                    <Text
                                        style={{
                                            fontSize: 10,
                                            color: labelColors[item.title] || "#888",
                                            display: "block",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {item.title}
                                    </Text>
                                    <Text
                                        strong
                                        style={{
                                            fontSize: 13,
                                            color: "#222",
                                            display: "block",
                                        }}
                                    >
                                        <CountUp
                                            end={Number(item.value)}
                                            duration={0.8}
                                            formattingFn={(val) =>
                                                typeof val === "number"
                                                    ? val.toString().padStart(2, "0")
                                                    : val
                                            }
                                        />
                                    </Text>
                                </div>
                            </Card>
                        </Col>
                    ))}
                </Row>
            </Spin>
        </Card>
    );
};

export default DashboardRow;
